import { useEffect, useState } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import TextArea from '@/design_system/TextArea';
import Paragraph from '@/design_system/Typography/Paragraph';
import { RequestWithRelationsFromRequestsList, useBulkArchiveRequests } from '@/models/request';
import { getArchiveSkipCounts } from '@/routes/Requests/components/BulkActionBar/getBulkEligibility';

import { BulkConfirmationDialog } from './BulkConfirmationDialog';

export const BulkArchiveDialog = ({
  showDialog,
  setShowDialog,
  selectedRequests,
  eligibleRequests,
  onSuccess,
}: {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  selectedRequests: RequestWithRelationsFromRequestsList[];
  eligibleRequests: RequestWithRelationsFromRequestsList[];
  onSuccess: () => void;
}) => {
  const { t } = useLingui();
  const [reason, setReason] = useState('');
  const { mutateAsync: archiveRequests, isPending } = useBulkArchiveRequests();

  const eligibleCount = eligibleRequests.length;
  const skipCounts = getArchiveSkipCounts(selectedRequests, eligibleRequests);
  const alreadyArchivedCount = skipCounts.alreadyArchived;
  const noPermissionCount = skipCounts.forbidden;

  useEffect(() => {
    if (showDialog) {
      setReason('');
    }
  }, [showDialog]);

  return (
    <BulkConfirmationDialog
      title={<Trans id="requests.bulk.archive.title">Archive requests</Trans>}
      confirm={<Trans id="requests.bulk.archive.confirm">Archive</Trans>}
      confirmButtonVariant="danger"
      showDialog={showDialog}
      setShowDialog={setShowDialog}
      isPending={isPending}
      isConfirmDisabled={eligibleCount === 0}
      requests={selectedRequests}
      onCloseAfterResult={onSuccess}
      onConfirm={() =>
        archiveRequests({
          requestIds: eligibleRequests.map((request) => request.id),
          reason: reason.trim() || undefined,
        })
      }
    >
      <Stack gap="1rem">
        <Stack gap="0.5rem">
          <Paragraph>
            <Plural
              id="requests.bulk.archive.scope"
              value={eligibleCount}
              one="You'll archive # request"
              other="You'll archive # requests"
            />
          </Paragraph>
          {alreadyArchivedCount > 0 && (
            <Message type="neutral">
              <Plural
                id="requests.bulk.archive.skip.already-archived"
                value={alreadyArchivedCount}
                one="# request is already archived"
                other="# requests are already archived"
              />
            </Message>
          )}
          {noPermissionCount > 0 && (
            <Message type="neutral">
              <Plural
                id="requests.bulk.archive.skip.forbidden"
                value={noPermissionCount}
                one="# request you don't have permission to archive"
                other="# requests you don't have permission to archive"
              />
            </Message>
          )}
        </Stack>
        <TextArea
          ariaLabel={t({
            id: 'requests.edit.archival-modal.reason.label',
            message: 'Specify the reason...',
          })}
          placeholder={t({
            id: 'requests.edit.archival-modal.details.label',
            message: 'Specify the reason...',
          })}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
        />
      </Stack>
    </BulkConfirmationDialog>
  );
};
